import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import useEscapeKey from '../../hooks/useEscapeKey';
import { MdCheckCircle, MdBlock, MdFilterList, MdRocketLaunch, MdPayments } from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';
import * as subSvc from '../../services/subscriptionService';
import { getStatusBadge } from '../../utils/clubStatus';
import { toWhatsAppPhone, openWhatsApp } from '../../utils/whatsapp';

const KNOWN_PLANS = ['free', 'pro'];

const waDay = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

// Reminder message, tone chosen from the club's status/dates.
const subscriptionWaMessage = ({ ownerName, clubName, amount, dueDate, overdue }) => {
  const who = ownerName || 'there';
  const amt = amount ? `NPR ${Number(amount).toLocaleString()}` : 'your subscription fee';
  if (overdue) {
    return `Namaste ${who}, your TrainAxis subscription for ${clubName} (${amt}) is now overdue${dueDate ? ` (was due ${waDay(dueDate)})` : ''}. Please complete payment to restore full access, or reply here if you need any help. Thank you!`;
  }
  return `Namaste ${who}, this is a friendly reminder that your TrainAxis subscription for ${clubName} (${amt}) is due on ${waDay(dueDate)}. Please complete payment to keep your club active. Thank you!`;
};

// Acknowledgement for a payment awaiting verification.
const paymentAckWaMessage = ({ ownerName, clubName, amount }) =>
  `Namaste ${ownerName || 'there'}, we've received your TrainAxis subscription payment for ${clubName}${amount ? ` (NPR ${Number(amount).toLocaleString()})` : ''} and are verifying it now. We'll confirm shortly. Thank you!`;

// Small WhatsApp icon-button — renders nothing when there is no usable phone.
function WhatsAppButton({ phone, message, title = 'Message on WhatsApp' }) {
  if (!toWhatsAppPhone(phone)) return null;
  return (
    <button
      onClick={(e) => { e.stopPropagation(); openWhatsApp(phone, message); }}
      title={title}
      aria-label={title}
      className="flex items-center justify-center w-7 h-7 rounded-lg transition-colors flex-shrink-0"
      style={{ background: 'rgba(37,211,102,0.12)', border: '1px solid rgba(37,211,102,0.4)', color: '#25D366' }}
    >
      <FaWhatsapp className="text-sm" />
    </button>
  );
}

// Club status pills come from the shared getStatusBadge() map (imported above).

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// Platform payment status pills — dashboard tint treatment.
const PAY_STATUS = {
  pending:  { label: 'Pending verification', bg: 'rgba(251,191,36,0.15)',  color: '#fbbf24' },
  verified: { label: 'Verified',             bg: 'rgba(52,211,153,0.12)',  color: '#34d399' },
  rejected: { label: 'Rejected',             bg: 'rgba(248,113,113,0.12)', color: '#f87171' },
};

// Consistent outline button (View Proof, etc.) — matches the ClubDetail secondary buttons.
const OUTLINE_BTN = { background: 'transparent', border: '1px solid rgba(237,232,224,0.10)' };

// Reject-payment reason modal (replaces window.prompt) — matches the ClubDetail SuspendModal style.
function RejectPaymentModal({ payment, onClose, onConfirm, pending }) {
  const [reason, setReason] = useState('');
  const valid = reason.trim().length > 0;
  useEscapeKey(onClose, !pending);
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => !pending && onClose()}>
      <div className="w-full max-w-md rounded-2xl p-6 space-y-4" style={{ background: '#1e1d1b', border: '1px solid rgba(237,232,224,0.08)' }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#7f1d1d' }}>
            <MdBlock className="text-red-400 text-lg" />
          </div>
          <div>
            <h2 className="font-bold text-[#ede8e0]">Reject Payment</h2>
            <p className="text-xs" style={{ color: '#8a847c' }}>
              {payment.club?.name || 'Club'} · NPR {Number(payment.amount).toLocaleString()} · The club admin will be notified
            </p>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-[#c5bfb5] mb-1.5">Reason for rejection *</label>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
            placeholder="e.g. Proof does not match the amount; reference not found"
            className="w-full border rounded-xl px-4 py-3 text-sm outline-none text-[#ede8e0] placeholder-[#6b6560] resize-none"
            style={{ background: '#1a1918', borderColor: 'rgba(237,232,224,0.08)' }} />
        </div>
        <div className="flex gap-2 pt-2">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium border transition-colors"
            style={{ borderColor: 'rgba(237,232,224,0.10)', color: '#c5bfb5' }}>
            Cancel
          </button>
          <button onClick={() => valid && onConfirm(reason.trim())} disabled={!valid || pending}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-[#ede8e0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: '#c8102e' }}>
            {pending ? 'Rejecting…' : 'Reject Payment'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Records a payment received OUTSIDE the app (e.g. a direct bank transfer) against a club.
// Shows the club's CURRENT expiry right next to the extend checkbox, so the superadmin always
// sees what they're about to change before confirming — the whole point being to avoid silently
// double-counting a date that was already corrected some other way (e.g. a manual Reactivate).
const PLATFORM_METHOD_LABELS = { bank: 'Bank Transfer', cash: 'Cash', esewa: 'eSewa', khalti: 'Khalti', fonepay: 'Fonepay' };
function RecordPaymentModal({ club, onClose, onConfirm, pending }) {
  const today = new Date().toISOString().slice(0, 10);
  const [amount, setAmount] = useState('');
  const [paidAt, setPaidAt] = useState(today);
  const [method, setMethod] = useState('bank');
  const [referenceId, setReferenceId] = useState('');
  const [notes, setNotes] = useState('');
  const [extend, setExtend] = useState(true);
  useEscapeKey(onClose, !pending);

  const currentExpiry = club.nextPaymentDue || club.subscriptionEndDate;
  const expiryLabel = currentExpiry
    ? new Date(currentExpiry).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : 'not set';

  const numAmount = Number(amount);
  const valid = Number.isFinite(numAmount) && numAmount > 0 && !!paidAt;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => !pending && onClose()}>
      <div className="w-full max-w-md rounded-2xl p-6 space-y-4" style={{ background: '#1e1d1b', border: '1px solid rgba(237,232,224,0.08)' }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#1e3a5f' }}>
            <MdPayments className="text-blue-400 text-lg" />
          </div>
          <div>
            <h2 className="font-bold text-[#ede8e0]">Record a Payment</h2>
            <p className="text-xs" style={{ color: '#8a847c' }}>{club.name} · received outside the app — no proof needed, you're confirming it yourself</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-[#c5bfb5] mb-1.5">Amount (NPR) *</label>
            <input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
              placeholder="2000"
              className="w-full border rounded-xl px-4 py-2.5 text-sm outline-none text-[#ede8e0] placeholder-[#6b6560]"
              style={{ background: '#1a1918', borderColor: 'rgba(237,232,224,0.08)' }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#c5bfb5] mb-1.5">Date received *</label>
            <input type="date" value={paidAt} max={today} onChange={e => setPaidAt(e.target.value)}
              className="w-full border rounded-xl px-4 py-2.5 text-sm outline-none text-[#ede8e0]"
              style={{ background: '#1a1918', borderColor: 'rgba(237,232,224,0.08)' }} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-[#c5bfb5] mb-1.5">Method</label>
            <select value={method} onChange={e => setMethod(e.target.value)}
              className="w-full border rounded-xl px-4 py-2.5 text-sm outline-none text-[#ede8e0]"
              style={{ background: '#1a1918', borderColor: 'rgba(237,232,224,0.08)' }}>
              {Object.entries(PLATFORM_METHOD_LABELS).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#c5bfb5] mb-1.5">Reference (optional)</label>
            <input value={referenceId} onChange={e => setReferenceId(e.target.value)}
              placeholder="Bank reference / txn ID"
              className="w-full border rounded-xl px-4 py-2.5 text-sm outline-none text-[#ede8e0] placeholder-[#6b6560]"
              style={{ background: '#1a1918', borderColor: 'rgba(237,232,224,0.08)' }} />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#c5bfb5] mb-1.5">Note (optional)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
            placeholder="e.g. Paid by bank transfer directly, couldn't log in while suspended"
            className="w-full border rounded-xl px-4 py-3 text-sm outline-none text-[#ede8e0] placeholder-[#6b6560] resize-none"
            style={{ background: '#1a1918', borderColor: 'rgba(237,232,224,0.08)' }} />
        </div>

        <label className="flex items-start gap-2.5 rounded-xl px-3.5 py-3 cursor-pointer" style={{ background: '#1a1918', border: '1px solid rgba(237,232,224,0.08)' }}>
          <input type="checkbox" checked={extend} onChange={e => setExtend(e.target.checked)} className="mt-0.5" />
          <span className="text-sm" style={{ color: '#c5bfb5' }}>
            Also extend subscription by the paid period (one calendar month)
            <span className="block text-xs mt-0.5" style={{ color: '#8a847c' }}>
              Current expiry: <strong style={{ color: '#ede8e0' }}>{expiryLabel}</strong>
              {extend ? ' — will move forward by one month from there.' : ' — will stay exactly as-is.'}
            </span>
          </span>
        </label>

        <div className="flex gap-2 pt-2">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-medium border transition-colors"
            style={{ borderColor: 'rgba(237,232,224,0.10)', color: '#c5bfb5' }}>
            Cancel
          </button>
          <button
            onClick={() => valid && onConfirm({ amount: numAmount, paidAt, method, referenceId: referenceId.trim(), notes: notes.trim(), extendSubscription: extend })}
            disabled={!valid || pending}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-[#ede8e0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: '#2563eb' }}>
            {pending ? 'Recording…' : 'Record Payment'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Platform Revenue tab — same visual language as the rest of this page (superadmin is
// always dark, inline hex, no semantic tokens). ─────────────────────────────────────────
const fmtNPR0 = (n) => `NPR ${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

const REV_CHART = {
  bar:  '#34d399',                    // emerald-400, matches "money in" everywhere else
  grid: 'rgba(237,232,224,0.08)',
  axis: '#6b6560',
  tipBg: '#1a1918',
  tipBorder: 'rgba(237,232,224,0.10)',
  tipText: '#ede8e0',
};

function RevenueTile({ label, value, sub, accent }) {
  return (
    <div className="rounded-xl border p-4" style={{ background: '#1e1d1b', borderColor: 'rgba(237,232,224,0.08)' }}>
      <p className="text-xs" style={{ color: '#8a847c' }}>{label}</p>
      <p className="text-2xl font-bold mt-1.5" style={{ color: accent || '#ede8e0' }}>{value}</p>
      {sub && <p className="text-xs mt-1" style={{ color: '#6b6560' }}>{sub}</p>}
    </div>
  );
}

function RevenueTrendChart({ data }) {
  if (!data || data.every(d => d.total === 0)) {
    return (
      <div className="rounded-xl border p-6 text-center" style={{ background: '#1e1d1b', borderColor: 'rgba(237,232,224,0.08)' }}>
        <p className="text-sm" style={{ color: '#6b6560' }}>No collected payments in the last 6 months yet.</p>
      </div>
    );
  }
  return (
    <div className="rounded-xl border p-4" style={{ background: '#1e1d1b', borderColor: 'rgba(237,232,224,0.08)' }}>
      <h3 className="text-sm font-bold mb-4" style={{ color: '#ede8e0' }}>Collected per month — last 6 months</h3>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid stroke={REV_CHART.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: REV_CHART.axis }} />
          <YAxis
            axisLine={false} tickLine={false}
            tick={{ fontSize: 11, fill: REV_CHART.axis }}
            tickFormatter={(v) => `NPR ${(v / 1000).toFixed(0)}k`}
          />
          <Tooltip
            formatter={(v) => fmtNPR0(v)}
            contentStyle={{ backgroundColor: REV_CHART.tipBg, border: `1px solid ${REV_CHART.tipBorder}`, borderRadius: '8px' }}
            labelStyle={{ color: REV_CHART.tipText }}
            cursor={{ fill: 'rgba(237,232,224,0.04)' }}
          />
          <Bar dataKey="total" fill={REV_CHART.bar} radius={[4, 4, 0, 0]} name="Total collected" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function PlatformRevenueTab() {
  const { data, isPending } = useQuery({
    queryKey: ['sa-platform-revenue'],
    queryFn:  () => subSvc.getPlatformRevenue().then(r => r.data),
  });

  if (isPending) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-24 rounded-xl animate-pulse" style={{ background: '#1e1d1b' }} />
        ))}
      </div>
    );
  }

  const clubTotals = data?.clubTotals || [];
  const trend       = data?.trend || [];
  const pending     = data?.pendingReview || { count: 0, amount: 0 };

  return (
    <div className="space-y-4">
      <p className="text-xs" style={{ color: '#6b6560' }}>
        Verified subscription payments only — pending and rejected payments are never counted below.
      </p>

      {/* Tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <RevenueTile label="Total collected" value={fmtNPR0(data?.totalCollected)} sub="All clubs, all-time" />
        <RevenueTile label="Collected this month" value={fmtNPR0(data?.collectedThisMonth)} accent="#34d399" />
        <RevenueTile
          label="Pending review"
          value={pending.count}
          sub={pending.count > 0 ? `${fmtNPR0(pending.amount)} awaiting verification` : 'Nothing awaiting verification'}
          accent={pending.count > 0 ? '#fbbf24' : undefined}
        />
      </div>

      {/* Per-club table */}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(237,232,224,0.08)' }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[480px]">
            <thead>
              <tr style={{ background: '#1a1918', borderBottom: '1px solid rgba(237,232,224,0.08)' }}>
                {['Club', 'Total Paid', 'Plan'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: '#6b6560' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {clubTotals.length === 0 ? (
                <tr><td colSpan={3} className="px-4 py-8 text-center text-sm" style={{ color: '#6b6560' }}>No verified payments yet</td></tr>
              ) : clubTotals.map(c => (
                <tr key={c.clubId} style={{ borderBottom: '1px solid rgba(237,232,224,0.05)' }}>
                  <td className="px-4 py-3 font-medium" style={{ color: '#ede8e0' }}>{c.name}</td>
                  <td className="px-4 py-3 font-semibold" style={{ color: '#34d399' }}>{fmtNPR0(c.total)}</td>
                  <td className="px-4 py-3 text-xs capitalize" style={{ color: '#8a847c' }}>{c.plan || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Trend */}
      <RevenueTrendChart data={trend} />
    </div>
  );
}

export default function Subscriptions() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter,   setPlanFilter]   = useState('');
  const [rejectTarget, setRejectTarget] = useState(null); // pending payment being rejected
  const [recordPayTarget, setRecordPayTarget] = useState(null); // club being manually recorded against
  const [proofView,    setProofView]    = useState(null); // { url } for image preview
  const [payTab,       setPayTab]       = useState('pending'); // 'pending' | 'history'
  const [histStatus,   setHistStatus]   = useState('all');

  const { data, isPending } = useQuery({
    queryKey: ['sa-subscriptions', statusFilter, planFilter],
    queryFn:  () => subSvc.getAllSubscriptions({
      status: statusFilter || undefined,
      plan:   planFilter   || undefined,
      limit:  100,
    }).then(r => r.data),
  });

  // Single source of truth for the Pro price is backend/config/plans.js — fetched here rather
  // than duplicated as a literal, so this page can never drift from it.
  const { data: plansData } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn:  () => subSvc.getPlans().then(r => r.data.plans),
    staleTime: 1000 * 60 * 10,
  });
  const proPrice = plansData?.pro?.price ?? 0;

  // Suspend + Set-Pro now use the dedicated superadmin handlers (same as ClubDetail), which keep
  // status/subscriptionStatus/dates/maxMembers in sync and are audited SA_SUSPEND_CLUB /
  // SA_UPDATE_SUBSCRIPTION. (The old updateClubSubscription path is no longer called from here.)
  const suspendMutation = useMutation({
    mutationFn: ({ clubId }) => subSvc.suspendClub(clubId),
    onSuccess:  () => { toast.success('Club suspended'); qc.invalidateQueries({ queryKey: ['sa-subscriptions'] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Suspend failed'),
  });

  const setProMutation = useMutation({
    mutationFn: ({ clubId }) => subSvc.setClubSubscription(clubId, { plan: 'pro' }),
    onSuccess:  () => { toast.success('Upgraded to Pro'); qc.invalidateQueries({ queryKey: ['sa-subscriptions'] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Upgrade failed'),
  });

  // Reactivate = un-suspend + push the paid period forward 30 days (KTM-aligned, server-side).
  // Surface the resulting expiry so it's obvious the date moved forward.
  const reactivateMutation = useMutation({
    mutationFn: ({ clubId }) => subSvc.reactivateClub(clubId, 30).then(r => r.data),
    onSuccess:  (data) => {
      const exp  = data?.newExpiry || data?.club?.subscriptionEndDate;
      const when = exp ? new Date(exp).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : null;
      toast.success(when ? `Reactivated — active until ${when}` : 'Club reactivated');
      qc.invalidateQueries({ queryKey: ['sa-subscriptions'] });
    },
    onError:    (e) => toast.error(e.response?.data?.message || 'Reactivate failed'),
  });

  // Records a payment received outside the app (e.g. a direct bank transfer) — creates a
  // verified PlatformSubscriptionPayment with no proof file, since the superadmin is vouching
  // for money they personally confirmed. Only extends the subscription date when the modal's
  // checkbox is on, so a date already corrected some other way (a manual Reactivate) isn't
  // double-counted.
  const recordPayMutation = useMutation({
    mutationFn: ({ clubId, data }) => subSvc.recordManualPlatformPayment(clubId, data).then(r => r.data),
    onSuccess:  (data) => {
      toast.success(data?.message || 'Payment recorded');
      qc.invalidateQueries({ queryKey: ['sa-subscriptions'] });
      qc.invalidateQueries({ queryKey: ['sa-platform-revenue'] });
      qc.invalidateQueries({ queryKey: ['sa-platform-payments'] });
      qc.invalidateQueries({ queryKey: ['sa-platform-payment-history'] });
      setRecordPayTarget(null);
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Could not record the payment'),
  });

  // Pending platform payments (clubs paying TrainAxis) awaiting verification
  const { data: payData } = useQuery({
    queryKey: ['sa-platform-payments'],
    queryFn:  () => subSvc.getPlatformPayments({ status: 'pending' }).then(r => r.data),
  });
  const pendingPayments = payData?.payments || [];

  const verifyPayMut = useMutation({
    mutationFn: (id) => subSvc.verifyPlatformPayment(id),
    onSuccess:  () => {
      toast.success('Payment verified. Club access restored.');
      qc.invalidateQueries({ queryKey: ['sa-platform-payments'] });
      qc.invalidateQueries({ queryKey: ['sa-subscriptions'] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to verify'),
  });

  const rejectPayMut = useMutation({
    mutationFn: ({ id, reason }) => subSvc.rejectPlatformPayment(id, reason),
    onSuccess:  () => {
      toast.success('Payment rejected and club admin notified.');
      qc.invalidateQueries({ queryKey: ['sa-platform-payments'] });
    },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to reject'),
  });

  const clubs = data?.clubs || [];

  // Full payment history (all statuses) — loaded when the History tab is active.
  const { data: histData } = useQuery({
    queryKey: ['sa-platform-payment-history', histStatus],
    queryFn:  () => subSvc.getPlatformPayments({ status: histStatus }).then(r => r.data),
    enabled:  payTab === 'history',
  });
  const historyPayments = histData?.payments || [];

  // Image proofs open in a preview modal; PDFs open/download in a new tab.
  const viewProof = (p) => {
    if (!p.proofUrl) return;
    const isPdf = (p.proofMime || '').includes('pdf') || /\.pdf($|\?)/i.test(p.proofUrl);
    if (isPdf) window.open(p.proofUrl, '_blank', 'noopener,noreferrer');
    else setProofView({ url: p.proofUrl });
  };

  // Escape closes the proof preview (it already closes on backdrop click).
  useEscapeKey(() => setProofView(null), !!proofView);

  return (
    <div className="space-y-5 max-w-7xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#ede8e0]">Subscriptions</h1>
          <p className="text-sm mt-0.5" style={{ color: '#8a847c' }}>
            {data?.total ?? '—'} clubs total
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <MdFilterList style={{ color: '#6b6560' }} />

          {/* Plan filter */}
          <select
            value={planFilter}
            onChange={e => setPlanFilter(e.target.value)}
            className="text-xs rounded-lg px-2.5 py-1.5 outline-none"
            style={{ background: '#1a1918', color: '#c5bfb5', border: '1px solid rgba(237,232,224,0.08)' }}
          >
            <option value="">All plans</option>
            <option value="free">Free</option>
            <option value="pro">Pro</option>
          </select>

          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="text-xs rounded-lg px-2.5 py-1.5 outline-none"
            style={{ background: '#1a1918', color: '#c5bfb5', border: '1px solid rgba(237,232,224,0.08)' }}
          >
            <option value="">All statuses</option>
            <option value="trialing">Trialing</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
      </div>

      {/* Subscription payments — pending queue + full history (records never deleted) */}
      <div id="pending-payments" className="rounded-xl p-5 border" style={{ background: '#1e1d1b', borderColor: 'rgba(237,232,224,0.08)' }}>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex gap-1 rounded-xl p-1" style={{ background: '#1a1918' }}>
            {[['pending', 'Pending Payments'], ['history', 'Payment History'], ['revenue', 'Platform Revenue']].map(([key, label]) => (
              <button key={key} onClick={() => setPayTab(key)}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5"
                style={payTab === key ? { background: '#28261f', color: '#ede8e0' } : { color: '#8a847c' }}>
                {key === 'revenue' && <MdPayments className="text-xs" />}
                {label}
                {key === 'pending' && pendingPayments.length > 0 && (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                    style={{ background: 'rgba(251,191,36,0.15)', color: '#fbbf24' }}>
                    {pendingPayments.length}
                  </span>
                )}
              </button>
            ))}
          </div>
          {payTab === 'history' && (
            <select value={histStatus} onChange={e => setHistStatus(e.target.value)}
              className="text-xs rounded-lg px-2.5 py-1.5 outline-none"
              style={{ background: '#1a1918', color: '#c5bfb5', border: '1px solid rgba(237,232,224,0.08)' }}>
              <option value="all">All statuses</option>
              <option value="pending">Pending verification</option>
              <option value="verified">Verified</option>
              <option value="rejected">Rejected</option>
            </select>
          )}
        </div>

        {payTab === 'revenue' ? (
          <PlatformRevenueTab />
        ) : payTab === 'pending' ? (
          pendingPayments.length === 0 ? (
            <p className="text-sm py-4" style={{ color: '#6b6560' }}>No pending subscription payments.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]">
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(237,232,224,0.08)' }}>
                    {['Club', 'Plan', 'Amount', 'Method', 'Reference ID', 'Submitted', 'Status', 'Proof', 'Actions'].map(h => (
                      <th key={h} className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: '#6b6560' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pendingPayments.map(p => (
                    <tr key={p.id} style={{ borderBottom: '1px solid rgba(237,232,224,0.05)' }}>
                      <td className="px-3 py-2.5">
                        <div className="font-medium text-[#ede8e0]">{p.club?.name || 'Unknown club'}</div>
                        {p.club?.slug && <div className="text-xs" style={{ color: '#6b6560' }}>{p.club.slug}</div>}
                        {p.notes && <div className="text-xs mt-0.5 italic" style={{ color: '#6b6560' }}>“{p.notes}”</div>}
                      </td>
                      <td className="px-3 py-2.5 capitalize" style={{ color: '#c5bfb5' }}>{p.plan}</td>
                      <td className="px-3 py-2.5 text-[#ede8e0] font-medium">
                        NPR {Number(p.amount).toLocaleString()}
                        {p.months > 1 && (
                          <div className="text-xs font-normal" style={{ color: '#8a847c' }}>
                            {p.months} months{p.discountPercent ? ` · ${p.discountPercent}% off` : ''}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 capitalize" style={{ color: '#c5bfb5' }}>{p.method}</td>
                      <td className="px-3 py-2.5" style={{ color: '#8a847c' }}>{p.referenceId || '—'}</td>
                      <td className="px-3 py-2.5" style={{ color: '#8a847c' }}>{fmtDate(p.submittedAt)}</td>
                      <td className="px-3 py-2.5">
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: 'rgba(251,191,36,0.15)', color: '#fbbf24' }}>Pending</span>
                      </td>
                      <td className="px-3 py-2.5">
                        {p.proofUrl ? (
                          <button onClick={() => viewProof(p)}
                            className="text-xs font-semibold px-3 py-1.5 rounded-lg" style={{ ...OUTLINE_BTN, color: '#c5bfb5' }}>
                            View Proof
                          </button>
                        ) : <span className="text-xs" style={{ color: '#6b6560' }}>None</span>}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => verifyPayMut.mutate(p.id)}
                            disabled={verifyPayMut.isPending}
                            className="text-xs font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50 border"
                            style={{ background: 'transparent', borderColor: 'rgba(52,211,153,0.4)', color: '#34d399' }}>
                            Verify
                          </button>
                          <button
                            onClick={() => setRejectTarget(p)}
                            disabled={rejectPayMut.isPending}
                            className="text-xs font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50 border"
                            style={{ background: 'transparent', borderColor: 'rgba(248,113,113,0.4)', color: '#f87171' }}>
                            Reject
                          </button>
                          <WhatsAppButton
                            phone={p.club?.ownerPhone}
                            message={paymentAckWaMessage({ ownerName: p.club?.ownerName, clubName: p.club?.name, amount: p.amount })}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          historyPayments.length === 0 ? (
            <p className="text-sm py-4" style={{ color: '#6b6560' }}>No subscription payments yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(237,232,224,0.08)' }}>
                    {['Club', 'Plan', 'Amount', 'Method', 'Reference ID', 'Submitted', 'Reviewed', 'Status', 'By', 'Proof'].map(h => (
                      <th key={h} className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: '#6b6560' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {historyPayments.map(p => {
                    const ss = PAY_STATUS[p.status] || PAY_STATUS.pending;
                    return (
                      <tr key={p.id} style={{ borderBottom: '1px solid rgba(237,232,224,0.05)' }}>
                        <td className="px-3 py-2.5">
                          <div className="font-medium text-[#ede8e0]">{p.club?.name || 'Unknown club'}</div>
                          {p.status === 'rejected' && p.rejectionReason && (
                            <div className="text-xs mt-0.5" style={{ color: '#fca5a5' }}>Reason: {p.rejectionReason}</div>
                          )}
                          {p.notes && <div className="text-xs mt-0.5 italic" style={{ color: '#6b6560' }}>“{p.notes}”</div>}
                        </td>
                        <td className="px-3 py-2.5 capitalize" style={{ color: '#c5bfb5' }}>{p.plan}</td>
                        <td className="px-3 py-2.5 text-[#ede8e0] font-medium">NPR {Number(p.amount).toLocaleString()}</td>
                        <td className="px-3 py-2.5 capitalize" style={{ color: '#c5bfb5' }}>{p.method}</td>
                        <td className="px-3 py-2.5" style={{ color: '#8a847c' }}>{p.referenceId || '—'}</td>
                        <td className="px-3 py-2.5" style={{ color: '#8a847c' }}>{fmtDate(p.submittedAt)}</td>
                        <td className="px-3 py-2.5" style={{ color: '#8a847c' }}>{p.verifiedAt ? fmtDate(p.verifiedAt) : '—'}</td>
                        <td className="px-3 py-2.5">
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: ss.bg, color: ss.color }}>{ss.label}</span>
                        </td>
                        <td className="px-3 py-2.5" style={{ color: '#8a847c' }}>{p.reviewedByName || '—'}</td>
                        <td className="px-3 py-2.5">
                          {p.proofUrl ? (
                            <button onClick={() => viewProof(p)}
                              className="text-xs font-semibold px-3 py-1.5 rounded-lg" style={{ ...OUTLINE_BTN, color: '#c5bfb5' }}>
                              View Proof
                            </button>
                          ) : <span className="text-xs" style={{ color: '#6b6560' }}>None</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {/* Table */}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(237,232,224,0.08)' }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[680px]">
            <thead>
              <tr style={{ background: '#1a1918', borderBottom: '1px solid rgba(237,232,224,0.08)' }}>
                {['Club', 'Plan / Price', 'Status', 'Members', 'Trial End / Next Payment', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide"
                    style={{ color: '#6b6560' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isPending ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(237,232,224,0.05)' }}>
                    {[1,2,3,4,5,6].map(j => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 rounded animate-pulse" style={{ background: '#28261f', width: j === 1 ? '120px' : '70px' }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : clubs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm" style={{ color: '#6b6560' }}>
                    No clubs found
                  </td>
                </tr>
              ) : clubs.map(club => {
                const statusStyle   = getStatusBadge(club.status);
                const isTrialing    = club.status === 'trial' || club.status === 'trialing';
                const endDate       = isTrialing ? club.trialEndsAt : club.nextPaymentDue;
                const isPro         = club.subscriptionPlan === 'pro';
                const hasDiscount   = club.activeDiscount && club.discountStatus === 'active';
                const basePrice     = club.basePrice ?? (isPro ? proPrice : 0);
                const discountAmt   = hasDiscount ? (club.discountAmount || 0) : 0;
                const finalPrice    = club.finalMonthlyPrice ?? basePrice;
                const planLabel     = KNOWN_PLANS.includes(club.subscriptionPlan) ? club.subscriptionPlan : null;
                // WhatsApp reminder variant: overdue if suspended or the due date has passed.
                const waOverdue     = club.status === 'suspended' || (endDate && new Date(endDate) < new Date());
                const waAmount      = isPro ? finalPrice : (basePrice || null);

                return (
                  <tr key={club._id}
                    onClick={() => navigate(`/superadmin/clubs/${club._id}`)}
                    className="hover:bg-[#28261f] transition-colors cursor-pointer"
                    style={{ borderBottom: '1px solid rgba(237,232,224,0.05)' }}>

                    {/* Club */}
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#ede8e0] text-sm">{club.name}</div>
                      <div className="text-xs mt-0.5" style={{ color: '#6b6560' }}>{club.slug}</div>
                    </td>

                    {/* Plan / Price */}
                    <td className="px-4 py-3">
                      {planLabel ? (
                        <>
                          <span className={`text-xs font-semibold capitalize ${isPro ? 'text-red-400' : 'text-[#8a847c]'}`}>
                            {planLabel}
                          </span>
                          {isPro && (
                            <div className="mt-1 space-y-0.5">
                              {hasDiscount ? (
                                <>
                                  <div className="text-xs line-through" style={{ color: '#6b6560' }}>
                                    NPR {basePrice.toLocaleString()}/mo
                                  </div>
                                  <div className="text-xs font-semibold" style={{ color: '#4ade80' }}>
                                    NPR {finalPrice.toLocaleString()}/mo
                                  </div>
                                  <div className="text-xs" style={{ color: '#f97316' }}>
                                    −NPR {discountAmt.toLocaleString()} discount
                                  </div>
                                </>
                              ) : (
                                <div className="text-xs" style={{ color: '#8a847c' }}>
                                  NPR {basePrice.toLocaleString()}/mo
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="text-xs font-semibold text-yellow-400" title="Unrecognised plan — not counted in MRR">
                          {club.subscriptionPlan || '—'} ⚠
                        </span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                        style={statusStyle.style}>
                        {statusStyle.label}
                      </span>
                      {club.trialDaysLeft !== null && club.trialDaysLeft !== undefined && (
                        <span className="block text-xs mt-0.5" style={{ color: club.trialDaysLeft <= 3 ? '#fca5a5' : '#8a847c' }}>
                          {club.trialDaysLeft}d left
                        </span>
                      )}
                    </td>

                    {/* Members (plan limit) */}
                    <td className="px-4 py-3 text-xs">
                      {club.maxMembers >= 9999 ? (
                        <span style={{ color: '#6b6560' }}>Unlimited</span>
                      ) : (
                        <span style={{ color: '#8a847c' }}>{club.maxMembers}</span>
                      )}
                    </td>

                    {/* Trial End / Next Payment */}
                    <td className="px-4 py-3 text-xs">
                      {endDate
                        ? <span style={{ color: '#8a847c' }}>{fmtDate(endDate)}</span>
                        : <span style={{ color: '#6b6560' }}>—</span>}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                        {/* Click-to-WhatsApp the owner (skipped when no phone) */}
                        <WhatsAppButton
                          phone={club.ownerPhone || club.contactPhone}
                          message={subscriptionWaMessage({
                            ownerName: club.ownerName, clubName: club.name,
                            amount: waAmount, dueDate: endDate, overdue: waOverdue,
                          })}
                        />

                        {/* Upgrade to Pro (only for free clubs) */}
                        {!isPro && (
                          <button
                            onClick={() => setProMutation.mutate({ clubId: club._id })}
                            disabled={setProMutation.isPending}
                            title="Upgrade to Pro (activates + 30-day paid period)"
                            className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg transition-colors font-semibold border disabled:opacity-50"
                            style={{ background: 'transparent', borderColor: 'rgba(248,113,113,0.4)', color: '#f87171' }}
                          >
                            <MdRocketLaunch className="text-xs" /> Pro
                          </button>
                        )}

                        {/* Suspend / Reactivate — labeled outline buttons */}
                        {club.status === 'suspended' ? (
                          <button
                            onClick={() => reactivateMutation.mutate({ clubId: club._id })}
                            disabled={reactivateMutation.isPending}
                            title="Reactivate and extend the subscription 30 days"
                            className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg transition-colors font-semibold border disabled:opacity-50"
                            style={{ background: 'transparent', borderColor: 'rgba(52,211,153,0.4)', color: '#34d399' }}
                          >
                            <MdCheckCircle className="text-sm" /> Reactivate
                          </button>
                        ) : (
                          <button
                            onClick={() => suspendMutation.mutate({ clubId: club._id })}
                            disabled={suspendMutation.isPending}
                            className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg transition-colors font-semibold border disabled:opacity-50"
                            style={{ background: 'transparent', borderColor: 'rgba(248,113,113,0.4)', color: '#f87171' }}
                          >
                            <MdBlock className="text-sm" /> Suspend
                          </button>
                        )}

                        {/* Record a payment received outside the app (e.g. direct bank transfer) */}
                        <button
                          onClick={() => setRecordPayTarget(club)}
                          title="Record a payment received outside the app"
                          className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg transition-colors font-semibold border disabled:opacity-50"
                          style={{ background: 'transparent', borderColor: 'rgba(96,165,250,0.4)', color: '#60a5fa' }}
                        >
                          <MdPayments className="text-sm" /> Record Payment
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {rejectTarget && (
        <RejectPaymentModal
          payment={rejectTarget}
          pending={rejectPayMut.isPending}
          onClose={() => setRejectTarget(null)}
          onConfirm={(reason) => rejectPayMut.mutate(
            { id: rejectTarget.id, reason },
            { onSuccess: () => setRejectTarget(null) }
          )}
        />
      )}

      {recordPayTarget && (
        <RecordPaymentModal
          club={recordPayTarget}
          pending={recordPayMutation.isPending}
          onClose={() => setRecordPayTarget(null)}
          onConfirm={(data) => recordPayMutation.mutate({ clubId: recordPayTarget._id, data })}
        />
      )}

      {/* Proof image preview */}
      {proofView && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setProofView(null)}>
          <div className="relative max-w-2xl max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setProofView(null)}
              className="absolute -top-3 -right-3 w-8 h-8 rounded-full flex items-center justify-center text-[#ede8e0]"
              style={{ background: '#c8102e' }}
              aria-label="Close"
            >
              ✕
            </button>
            <img src={proofView.url} alt="Payment proof" className="max-w-full max-h-[90vh] rounded-xl" />
            <div className="text-center mt-2">
              <a href={proofView.url} target="_blank" rel="noreferrer" className="text-xs" style={{ color: '#c8102e' }}>
                Open original in new tab
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
